import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";

const KIND = { in_use: "Используется салоном", pool: "Резерв (пул)", orphan: "Ничей (нет в базе)" };
const STATE_BADGE = { authorized: "bg-green-100 text-green-800", notAuthorized: "bg-amber-100 text-amber-800", starting: "bg-blue-100 text-blue-800",
  gone: "bg-gray-200 text-gray-700", unreachable: "bg-red-100 text-red-800" };

// Super-admin: every Green API instance on the partner account vs. our DB, with bulk delete of unused ones.
export default function AdminGreenInstances() {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState("");

  const audit = async () => {
    setBusy("audit"); setSel(new Set());
    try { const r = await axios.get(`${API}/admin/greenapi/instances/audit`, auth); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectUnused = () => setSel(new Set((data?.instances || []).filter((i) => i.kind === "orphan" || (i.kind === "in_use" && i.state !== "authorized" && !i.salon_paid)).map((i) => i.id_instance)));
  const remove = async () => {
    const ids = [...sel];
    if (!ids.length || !window.confirm(`Удалить ${ids.length} инстанс(ов) у Green API? Салоны потеряют WhatsApp и должны будут отсканировать QR заново. Отменить нельзя.`)) return;
    setBusy("delete");
    try {
      const r = await axios.post(`${API}/admin/greenapi/instances/delete`, { id_instances: ids }, auth);
      toast.success(`Удалено: ${r.data.deleted.length}${r.data.errors.length ? `, ошибок: ${r.data.errors.length}` : ""}`);
      if (r.data.errors.length) console.warn(r.data.errors);
      await audit();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const markDbOnly = async () => {
    const ids = (data?.db_only || []).map((x) => x.id_instance);
    if (!ids.length) return;
    await axios.post(`${API}/admin/greenapi/instances/mark-db-only-deleted`, { id_instances: ids }, auth);
    toast.success(`Помечено удалёнными в базе: ${ids.length}`); audit();
  };
  const s = data?.summary;

  return (
    <div className="text-black space-y-4" data-testid="admin-green-instances">
      <div className="flex flex-wrap gap-2 items-center">
        <Button className="bg-black text-white font-bold" onClick={audit} disabled={busy === "audit"} data-testid="green-audit-btn">
          <RefreshCw size={16} className={`mr-2 ${busy === "audit" ? "animate-spin" : ""}`} />{busy === "audit" ? "Опрашиваем Green API…" : "Проверить инстансы Green API"}
        </Button>
        {data && (
          <>
            <Button variant="outline" className="border-2 border-black" onClick={selectUnused} data-testid="green-select-unused-btn">Выбрать неиспользуемые</Button>
            <Button variant="outline" className="border-2 border-red-600 text-red-700" onClick={remove} disabled={!sel.size || busy === "delete"} data-testid="green-delete-btn">
              <Trash2 size={16} className="mr-2" />Удалить выбранные ({sel.size})
            </Button>
          </>
        )}
      </div>
      {s && (
        <div className="text-sm border-2 border-black rounded-lg p-3 bg-white" data-testid="green-audit-summary">
          На аккаунте Green API: <b>{s.total_remote}</b> · используется салонами: <b>{s.in_use}</b> · в пуле: <b>{s.pool}</b> · ничьих: <b className={s.orphan ? "text-red-700" : ""}>{s.orphan}</b> · авторизовано (WhatsApp подключён): <b>{s.authorized}</b>
          {s.db_only > 0 && <> · в базе, но нет у Green: <b className="text-amber-700">{s.db_only}</b> <button className="underline text-xs" onClick={markDbOnly}>пометить удалёнными</button></>}
          {s.api_url_fixed > 0 && <> · <span className="text-blue-700">обновлён api_url (переезд зоны): {s.api_url_fixed}</span></>}
          <div className="text-xs text-gray-500 mt-1">Хост партнёрского API: {data.partner_host}. «Ничей» = инстанс есть у Green, но не привязан ни к одному салону в нашей базе (напр. остался после удаления аккаунта). За каждый живой инстанс Green API берёт плату.</div>
        </div>
      )}
      {data && (
        <div className="overflow-x-auto border-2 border-black rounded-lg bg-white">
          <table className="w-full text-xs" data-testid="green-instances-table">
            <thead className="bg-gray-100"><tr>
              <th className="p-2"></th><th className="p-2 text-left">idInstance</th><th className="p-2 text-left">Тип</th><th className="p-2 text-left">Состояние</th>
              <th className="p-2 text-left">Салон</th><th className="p-2 text-left">Телефон</th><th className="p-2 text-left">Оплачен</th><th className="p-2 text-left">Создан</th>
            </tr></thead>
            <tbody>
              {data.instances.map((i) => (
                <tr key={i.id_instance} className={`border-t ${sel.has(i.id_instance) ? "bg-red-50" : ""}`} data-testid={`green-row-${i.id_instance}`}>
                  <td className="p-2"><input type="checkbox" checked={sel.has(i.id_instance)} onChange={() => toggle(i.id_instance)} /></td>
                  <td className="p-2 font-mono">{i.id_instance}<div className="text-gray-400 font-sans">{(i.api_url || "").replace("https://", "")}</div></td>
                  <td className="p-2">{KIND[i.kind]}</td>
                  <td className="p-2"><span className={`px-1.5 py-0.5 rounded ${STATE_BADGE[i.state] || "bg-gray-100"}`}>{i.state}</span>{i.db_state && i.db_state !== i.state ? <span className="text-gray-400"> (в базе: {i.db_state})</span> : null}</td>
                  <td className="p-2">{i.salon_name || (i.salon_id ? i.salon_id.slice(0, 8) : "—")}</td>
                  <td className="p-2">{i.phone || "—"}</td>
                  <td className="p-2">{i.salon_paid == null ? "—" : i.salon_paid ? "✅" : "❌"}</td>
                  <td className="p-2 text-gray-500">{(i.created || "").toString().slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
